import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import betClient from '../../api/betClient';
import { useAuth } from '../../contexts/AuthContext';
import { useEffectiveTier } from '../../hooks/useEffectiveTier';
import BetDashboard from './BetDashboard';
import BetLog from './BetLog';
import AddBetForm from './AddBetForm';
import BetAiReview from './BetAiReview';
import LockedFeatureCard from '../upgrade/LockedFeatureCard';

const SPORT_FILTERS = [
  { key: null, label: 'All' },
  { key: 'americanfootball_nfl', label: 'NFL' },
  { key: 'americanfootball_ncaaf', label: 'NCAAF' },
  { key: 'basketball_nba', label: 'NBA' },
  { key: 'basketball_ncaab', label: 'NCAAB' },
  { key: 'baseball_mlb', label: 'MLB' },
  { key: 'icehockey_nhl', label: 'NHL' },
  { key: 'soccer_usa_mls', label: 'MLS' },
  { key: 'mma_mixed_martial_arts', label: 'MMA' },
  { key: 'boxing_boxing', label: 'Boxing' },
];

const STATUS_FILTERS = [
  { key: null, label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'won', label: 'Won' },
  { key: 'lost', label: 'Lost' },
  { key: 'push', label: 'Push' },
];

const MARKET_FILTERS = [
  { key: null, label: 'All' },
  { key: 'spreads', label: 'Spread' },
  { key: 'h2h', label: 'ML' },
  { key: 'totals', label: 'Total' },
];

export default function BetTracker() {
  const { user, loading: authLoading } = useAuth();
  const tier = useEffectiveTier();

  const [dashboard, setDashboard] = useState(null);
  const [bets, setBets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [resolveResult, setResolveResult] = useState(null);

  const [sportFilter, setSportFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [marketFilter, setMarketFilter] = useState(null);

  const fetchData = useCallback(() => {
    if (!user) return;
    setLoading(true); setError(null);
    const dashParams = new URLSearchParams();
    if (sportFilter) dashParams.set('sport', sportFilter);

    const betParams = new URLSearchParams();
    if (sportFilter) betParams.set('sport', sportFilter);
    if (statusFilter) betParams.set('status', statusFilter);
    if (marketFilter) betParams.set('market', marketFilter);
    betParams.set('limit', '200');

    Promise.all([
      betClient.get(`/api/bets/dashboard?${dashParams.toString()}`),
      betClient.get(`/api/bets?${betParams.toString()}`),
    ])
      .then(([d, b]) => { setDashboard(d.data); setBets(b.data); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [user, sportFilter, statusFilter, marketFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  function handleResolve() {
    setResolving(true); setResolveResult(null);
    betClient.post('/api/bets/resolve')
      .then((r) => { setResolveResult(r.data); fetchData(); })
      .catch((e) => setResolveResult({ error: e.message }))
      .finally(() => setResolving(false));
  }

  function handleBetAdded() {
    setShowForm(false);
    fetchData();
  }

  // Auth gate
  if (authLoading) {
    return (
      <div className="px-5 py-6">
        <div className="space-y-2">{[...Array(4)].map((_, i) => <div key={i} className="h-16 rounded bg-[#F8F9FA] dark:bg-[#151a25] skeleton" />)}</div>
      </div>
    );
  }

  if (user && tier === 'free') {
    return (
      <div className="px-5 py-6">
        <LockedFeatureCard
          featureName="Bet Tracker"
          description="Log your bets, track your P&L, and get auto-resolution against game results."
          requiredTier="pro"
        />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="px-5 py-6">
        <div className="max-w-[400px] mx-auto mt-12">
          <div className="rounded-lg border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#151a25] px-6 py-8 text-center">
            <svg className="w-10 h-10 text-[#9CA3AF] dark:text-[#505a6e] mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <h2 className="text-[18px] font-semibold text-[#111827] dark:text-[#e2e8f0] mb-2" style={{ fontFamily: 'var(--font-sans)' }}>Sign in to track your bets</h2>
            <p className="text-[14px] text-[#6B7280] dark:text-[#8892a8] mb-5" style={{ fontFamily: 'var(--font-sans)' }}>Create an account to log bets, track P&L, and get AI-powered analysis of your betting history.</p>
            <Link to="/auth" className="inline-block px-5 py-2.5 rounded-md bg-[#005BB3] hover:bg-[#004494] text-white text-[14px] font-semibold transition-colors duration-150" style={{ fontFamily: 'var(--font-sans)' }}>
              Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-5 py-6">
      {/* Header */}
      <div className="mb-1">
        <p className="text-[12px] uppercase tracking-[1px] font-medium text-[#005BB3] dark:text-[#3b82f6]" style={{ fontFamily: 'var(--font-sans)' }}>Bet Tracker</p>
      </div>
      <h1 className="text-[22px] font-semibold text-[#111827] dark:text-[#e2e8f0] tracking-tight mb-1" style={{ fontFamily: 'var(--font-sans)' }}>Your Bets</h1>
      <p className="text-[14px] text-[#6B7280] dark:text-[#8892a8] mb-4" style={{ fontFamily: 'var(--font-sans)' }}>Track bets, auto-resolve results, and get AI-powered review of your betting history.</p>

      {/* Action bar */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <button onClick={() => setShowForm(!showForm)}
          className="px-3 py-1.5 rounded-md bg-[#005BB3] hover:bg-[#004494] text-white text-[13px] font-medium transition-colors duration-150" style={{ fontFamily: 'var(--font-sans)' }}>
          {showForm ? 'Cancel' : '+ Add Bet'}
        </button>
        <button onClick={handleResolve} disabled={resolving}
          className="px-3 py-1.5 rounded-md border border-[#E5E7EB] dark:border-[#1e2536] text-[13px] font-medium text-[#111827] dark:text-[#8892a8] hover:border-[#005BB3] hover:text-[#005BB3] transition-colors duration-150 disabled:opacity-40" style={{ fontFamily: 'var(--font-sans)' }}>
          {resolving ? 'Resolving...' : 'Resolve Pending'}
        </button>
        {resolveResult && !resolveResult.error && (
          <span className="text-[12px] text-[#029456]" style={{ fontFamily: 'var(--font-mono)' }}>
            {resolveResult.resolved} resolved, {resolveResult.still_pending} pending
          </span>
        )}
        {resolveResult?.error && (
          <span className="text-[12px] text-[#DC2626]">{resolveResult.error}</span>
        )}
      </div>

      {showForm && <AddBetForm onSuccess={handleBetAdded} onCancel={() => setShowForm(false)} />}

      {error && (
        <div className="rounded bg-[rgba(239,68,68,0.06)] dark:bg-[rgba(239,68,68,0.08)] border border-[#fecaca] dark:border-[#7f1d1d] px-3 py-2 mb-4">
          <p className="text-[#DC2626] text-[11px]">Failed to load: {error}</p>
          <button onClick={fetchData} className="text-[10px] text-[#DC2626] underline mt-1">Retry</button>
        </div>
      )}

      {loading ? (
        <div className="space-y-2">{[...Array(6)].map((_, i) => <div key={i} className="h-16 rounded bg-[#F8F9FA] dark:bg-[#151a25] skeleton" />)}</div>
      ) : (
        <>
          {dashboard && <BetDashboard stats={dashboard} bets={bets} />}

          {/* Filters */}
          <div className="flex flex-wrap items-end gap-4 mb-4 mt-6 border-b border-[#E5E7EB] dark:border-[#1e2536] pb-0">
            <FilterGroup label="Sport" options={SPORT_FILTERS} value={sportFilter} onChange={setSportFilter} />
            <div className="w-px h-5 bg-[#E5E7EB] dark:bg-[#1e2536] mb-2 flex-shrink-0" />
            <FilterGroup label="Status" options={STATUS_FILTERS} value={statusFilter} onChange={setStatusFilter} />
            <div className="w-px h-5 bg-[#E5E7EB] dark:bg-[#1e2536] mb-2 flex-shrink-0" />
            <FilterGroup label="Market" options={MARKET_FILTERS} value={marketFilter} onChange={setMarketFilter} />
          </div>

          <BetLog bets={bets} />
          <BetAiReview sport={sportFilter} />
        </>
      )}
    </div>
  );
}

function FilterGroup({ label, options, value, onChange }) {
  return (
    <div className="flex items-end gap-0">
      <span className="text-[11px] uppercase tracking-[1px] font-semibold text-[#9CA3AF] dark:text-[#505a6e] mr-3 pb-2 select-none" style={{ fontFamily: 'var(--font-sans)' }}>{label}</span>
      {options.map((o) => (
        <button key={o.label} onClick={() => onChange(o.key)}
          className={`px-0 mr-4 pb-2 text-[13px] whitespace-nowrap transition-colors duration-150 border-b-2 ${
            value === o.key
              ? 'font-semibold text-[#111827] dark:text-[#e2e8f0] border-[#005BB3] dark:border-[#3b82f6]'
              : 'font-medium text-[#6B7280] dark:text-[#8892a8] border-transparent hover:text-[#4B5563] dark:hover:text-[#e2e8f0]'
          }`}
          style={{ fontFamily: 'var(--font-sans)' }}>
          {o.label}
        </button>
      ))}
    </div>
  );
}
